import { TranslationKey } from './TranslationKey';

export const ENGLISH_TRANSLATION_URL = 'https://ventorystorageshared.blob.core.windows.net/translations/en.json';

export interface TranslationRecord {
  singular: {
    upper?: string;
    lower: string;
  };
  plural?: {
    upper?: string;
    lower: string;
  };
}

export const emptyTranslationRecord: TranslationRecord = { singular: { lower: '' } };

export type Translations = Record<TranslationKey, TranslationPluralType>;

export class TranslationPluralType {
  singular: TranslationCasingType;
  private _plural?: TranslationCasingType;

  qty(qty: number) {
    return qty === 1 ? this.singular : this.plural;
  }

  toRecord(): TranslationRecord {
    if (this.singular.lower === 'undefined translation') {
      return {
        singular: {
          lower: this.singular.lower,
        },
      };
    }

    const record: TranslationRecord = {
      singular: {
        lower: this.singular.lower,
      },
    };

    if (this.singular.rawUpper) record.singular.upper = this.singular.rawUpper;
    if (this._plural) {
      record.plural = { lower: this._plural.lower };
      if (this._plural.rawUpper) record.plural.upper = this._plural.rawUpper;
    }

    return record;
  }

  get plural() {
    if (this._plural) return this._plural;
    return this.singular;
  }

  constructor(singular: TranslationCasingType, plural?: TranslationCasingType) {
    this.singular = singular;
    this._plural = plural;
  }
}

export class TranslationCasingType {
  lower: string;
  private _upper?: string;

  set upper(upper: string) {
    this._upper = upper;
  }

  get rawUpper() {
    return this._upper;
  }

  get upper() {
    if (this._upper) return this._upper;
    if (!this.lower) return this.lower;
    return this.lower.charAt(0).toUpperCase() + this.lower.slice(1);
  }

  get label() {
    return this.lower.charAt(0).toUpperCase() + this.lower.slice(1);
  }

  constructor(lower: string, upper?: string) {
    this.lower = lower;
    this._upper = upper;
  }
}

export namespace TranslationUtils {
  function extractVariables(str: string) {
    return Array.from(str.matchAll(/\$\{([^\}]+)\}/g), match => match[1]);
  }

  export function checkVariables(english: string, string: string) {
    const required = extractVariables(english);
    const found = new Set(extractVariables(string));
    return required.every(v => found.has(v));
  }

  export const allKeys = [...Object.keys(TranslationKey)] as TranslationKey[];
}

export function translationsFromJson(json: any): Translations {
  const translations = {} as Record<TranslationKey, TranslationPluralType>;
  for (const key of Object.keys(TranslationKey)) {
    const translationKey = key as TranslationKey;
    const value = json[translationKey];

    const translationKeyRecord = new TranslationPluralType(
      new TranslationCasingType(translationKey),
      new TranslationCasingType(translationKey),
    );

    if (!value) {
      continue;
    }

    let singularCasingType: TranslationCasingType;
    const singular = value ? value['singular'] : undefined;
    if (singular) {
      singularCasingType = new TranslationCasingType(singular['lower'], singular['upper']);
    } else {
      console.warn(`Missing singular data for key ${translationKey}`);
      singularCasingType ||= translationKeyRecord.singular;
    }

    let pluralCasingType: TranslationCasingType | undefined;
    const plural = value ? value['plural'] : undefined;
    if (plural) {
      pluralCasingType = new TranslationCasingType(plural['lower'], plural['upper']);
    }

    translations[translationKey] = new TranslationPluralType(singularCasingType, pluralCasingType);
  }

  return translations;
}
